/**
 * Bible Reader Page - MVP Bible reading experience
 * Features: Book/Chapter navigation, Verse display, Notes, Highlights, Bookmarks
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { 
  Book, BookOpen, ChevronLeft, ChevronRight, Bookmark, PenLine, 
  Highlighter, Search, Settings, Sun, Moon, Menu, Home, Heart,
  Trophy, Calendar, Flame, X, Check, List
} from 'lucide-react';

const BiblePage = () => {
  const { isAuthenticated, user } = useAuthStore();
  const queryClient = useQueryClient();
  
  // Reading state
  const [selectedBook, setSelectedBook] = useState('John');
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [showBookSelector, setShowBookSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [darkMode, setDarkMode] = useState(false);
  const [fontSize, setFontSize] = useState('text-lg');
  
  // Note/highlight state
  const [noteContent, setNoteContent] = useState('');
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [highlightColor, setHighlightColor] = useState('yellow');
  
  // Fetch Bible books
  const { data: books = [] } = useQuery({
    queryKey: ['bible-books'],
    queryFn: () => api.get('/bible/books').then(r => r.data),
  });
  
  // Fetch current chapter
  const { data: chapterData, isLoading: loadingChapter } = useQuery({
    queryKey: ['bible-chapter', selectedBook, selectedChapter],
    queryFn: () => api.get(`/bible/${selectedBook}/${selectedChapter}`).then(r => r.data),
    enabled: !!selectedBook && !!selectedChapter,
  });
  
  // Fetch verse of the day
  const { data: votd } = useQuery({
    queryKey: ['verse-of-day'],
    queryFn: () => api.get('/bible/verse-of-the-day').then(r => r.data),
  });
  
  // Fetch user's highlights for this chapter
  const { data: highlights = [] } = useQuery({
    queryKey: ['bible-highlights', selectedBook],
    queryFn: () => api.get(`/bible/my-highlights?book=${selectedBook}`).then(r => r.data),
    enabled: !!isAuthenticated,
  });
  
  // Fetch user's notes
  const { data: notes = [] } = useQuery({
    queryKey: ['bible-notes', selectedBook],
    queryFn: () => api.get(`/bible/my-notes?book=${selectedBook}`).then(r => r.data),
    enabled: !!isAuthenticated,
  });
  
  // Fetch user's bookmarks
  const { data: bookmarks = [] } = useQuery({
    queryKey: ['bible-bookmarks'],
    queryFn: () => api.get('/bible/my-bookmarks').then(r => r.data),
    enabled: !!isAuthenticated,
  });
  
  // Fetch user stats
  const { data: stats } = useQuery({
    queryKey: ['bible-stats'],
    queryFn: () => api.get('/bible/my-stats').then(r => r.data),
    enabled: !!isAuthenticated,
  });
  
  // Fetch reading plans
  const { data: plans = [] } = useQuery({
    queryKey: ['reading-plans'],
    queryFn: () => api.get('/bible/plans').then(r => r.data),
  });
  
  // Fetch user's active plans
  const { data: myPlans = [] } = useQuery({
    queryKey: ['my-plans'],
    queryFn: () => api.get('/bible/my-plans').then(r => r.data),
    enabled: !!isAuthenticated,
  });
  
  // Create highlight mutation
  const createHighlight = useMutation({
    mutationFn: (data) => api.post('/bible/highlights', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['bible-highlights']);
      toast.success('Verse highlighted!');
    },
  });
  
  // Create note mutation
  const createNote = useMutation({
    mutationFn: (data) => api.post('/bible/notes', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['bible-notes']);
      toast.success('Note saved!');
      setShowNoteDialog(false);
      setNoteContent('');
    },
  });
  
  // Create bookmark mutation
  const createBookmark = useMutation({
    mutationFn: (data) => api.post('/bible/bookmarks', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['bible-bookmarks']);
      toast.success('Bookmark added!');
    },
  });
  
  // Get book info
  const currentBookInfo = books.find(b => b.name === selectedBook || b.abbr === selectedBook);
  const totalChapters = currentBookInfo?.chapters || 1;
  
  // Check if verse is highlighted
  const isHighlighted = (verse) => {
    return highlights.find(h => h.book === selectedBook && h.chapter === selectedChapter && h.verse === verse);
  };
  
  // Check if verse has note
  const hasNote = (verse) => {
    return notes.find(n => n.book === selectedBook && n.chapter === selectedChapter && n.verse === verse);
  };
  
  // Navigate chapters
  const goToPrevChapter = () => {
    if (selectedChapter > 1) {
      setSelectedChapter(selectedChapter - 1);
    } else {
      // Go to previous book
      const bookIndex = books.findIndex(b => b.name === selectedBook);
      if (bookIndex > 0) {
        const prevBook = books[bookIndex - 1];
        setSelectedBook(prevBook.name);
        setSelectedChapter(prevBook.chapters);
      }
    }
  };
  
  const goToNextChapter = () => {
    if (selectedChapter < totalChapters) {
      setSelectedChapter(selectedChapter + 1);
    } else {
      // Go to next book
      const bookIndex = books.findIndex(b => b.name === selectedBook);
      if (bookIndex < books.length - 1) {
        setSelectedBook(books[bookIndex + 1].name);
        setSelectedChapter(1);
      }
    }
  };
  
  // Handle verse tap
  const handleVerseTap = (verse) => {
    if (selectedVerse === verse.verse) {
      setSelectedVerse(null);
    } else {
      setSelectedVerse(verse.verse);
    }
  };
  
  // Handle highlight
  const handleHighlight = () => {
    if (!isAuthenticated) {
      toast.error('Please login to highlight verses');
      return;
    }
    createHighlight.mutate({
      book: selectedBook,
      chapter: selectedChapter,
      verse: selectedVerse,
      color: highlightColor
    });
    setSelectedVerse(null);
  };
  
  // Handle add note
  const handleAddNote = () => {
    if (!isAuthenticated) {
      toast.error('Please login to add notes');
      return;
    }
    createNote.mutate({
      book: selectedBook,
      chapter: selectedChapter,
      verse: selectedVerse,
      content: noteContent
    });
    setSelectedVerse(null);
  };
  
  // Handle bookmark
  const handleBookmark = () => {
    if (!isAuthenticated) {
      toast.error('Please login to bookmark');
      return;
    }
    createBookmark.mutate({
      book: selectedBook,
      chapter: selectedChapter,
      verse: selectedVerse
    });
    setSelectedVerse(null);
  };
  
  // Group books by testament
  const oldTestament = books.filter(b => b.testament === 'OT');
  const newTestament = books.filter(b => b.testament === 'NT');
  
  // Get highlight color class
  const getHighlightClass = (color) => {
    const colors = {
      yellow: 'bg-yellow-200 dark:bg-yellow-900/50',
      green: 'bg-green-200 dark:bg-green-900/50',
      blue: 'bg-blue-200 dark:bg-blue-900/50',
      purple: 'bg-purple-200 dark:bg-purple-900/50',
      pink: 'bg-pink-200 dark:bg-pink-900/50',
    };
    return colors[color] || colors.yellow;
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-slate-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left - Menu & Logo */}
            <div className="flex items-center gap-3">
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-indigo-600" />
                      Bible App
                    </SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-4">
                    <Button variant="ghost" className="w-full justify-start" onClick={() => setShowBookSelector(true)}>
                      <Book className="h-4 w-4 mr-2" />
                      Books
                    </Button>
                    <Button variant="ghost" className="w-full justify-start">
                      <Calendar className="h-4 w-4 mr-2" />
                      Reading Plans
                      {myPlans.length > 0 && <Badge className="ml-auto">{myPlans.length}</Badge>}
                    </Button>
                    <Button variant="ghost" className="w-full justify-start">
                      <Bookmark className="h-4 w-4 mr-2" />
                      Bookmarks
                      {bookmarks.length > 0 && <Badge variant="outline" className="ml-auto">{bookmarks.length}</Badge>}
                    </Button>
                    <Button variant="ghost" className="w-full justify-start">
                      <PenLine className="h-4 w-4 mr-2" />
                      Notes
                      {notes.length > 0 && <Badge variant="outline" className="ml-auto">{notes.length}</Badge>}
                    </Button>
                    <Button variant="ghost" className="w-full justify-start">
                      <Highlighter className="h-4 w-4 mr-2" />
                      Highlights
                    </Button>
                    <Button variant="ghost" className="w-full justify-start">
                      <Heart className="h-4 w-4 mr-2" />
                      Prayer Journal
                    </Button>
                    
                    {/* Stats */}
                    {isAuthenticated && stats && (
                      <div className="pt-4 border-t">
                        <h4 className="text-sm font-medium mb-3">Your Progress</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-lg p-3 text-center">
                            <Flame className="h-5 w-5 mx-auto mb-1" />
                            <div className="text-2xl font-bold">{stats.current_streak}</div>
                            <div className="text-xs opacity-80">Day Streak</div>
                          </div>
                          <div className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white rounded-lg p-3 text-center">
                            <Trophy className="h-5 w-5 mx-auto mb-1" />
                            <div className="text-2xl font-bold">{stats.longest_streak}</div>
                            <div className="text-xs opacity-80">Best Streak</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
              
              <h1 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {selectedBook} {selectedChapter}
              </h1>
            </div>
            
            {/* Right - Actions */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={() => setShowBookSelector(true)}>
                <List className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon">
                <Search className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setDarkMode(!darkMode)}>
                {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Verse of the Day Banner */}
      {votd && (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4">
          <div className="container mx-auto">
            <div className="text-center">
              <Badge variant="secondary" className="mb-2">Verse of the Day</Badge>
              <p className="text-lg italic">"{votd.text}"</p>
              <p className="text-sm mt-2 opacity-80">— {votd.book} {votd.chapter}:{votd.verse}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        {/* Book Selector Dialog */}
        <Dialog open={showBookSelector} onOpenChange={setShowBookSelector}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Select Book</DialogTitle>
            </DialogHeader>
            <Tabs defaultValue="ot">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="ot">Old Testament</TabsTrigger>
                <TabsTrigger value="nt">New Testament</TabsTrigger>
              </TabsList>
              <TabsContent value="ot" className="mt-4">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {oldTestament.map(book => (
                    <Button
                      key={book.name}
                      variant={selectedBook === book.name ? "default" : "outline"}
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        setSelectedBook(book.name);
                        setSelectedChapter(1);
                        setShowBookSelector(false);
                      }}
                    >
                      {book.abbr}
                    </Button>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="nt" className="mt-4">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {newTestament.map(book => (
                    <Button
                      key={book.name}
                      variant={selectedBook === book.name ? "default" : "outline"}
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        setSelectedBook(book.name);
                        setSelectedChapter(1);
                        setShowBookSelector(false);
                      }}
                    >
                      {book.abbr}
                    </Button>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
            
            {/* Chapter Selector */}
            {currentBookInfo && (
              <div className="mt-4 border-t pt-4">
                <h4 className="text-sm font-medium mb-2">Select Chapter</h4>
                <div className="grid grid-cols-6 sm:grid-cols-10 gap-2">
                  {Array.from({ length: totalChapters }, (_, i) => i + 1).map(ch => (
                    <Button
                      key={ch}
                      variant={selectedChapter === ch ? "default" : "outline"}
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        setSelectedChapter(ch);
                        setShowBookSelector(false);
                      }}
                    >
                      {ch}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Chapter Content */}
        <Card className={`${darkMode ? 'bg-slate-800 border-slate-700' : ''}`}>
          <CardContent className="pt-6">
            {loadingChapter ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading chapter...</p>
              </div>
            ) : chapterData?.verses?.length > 0 ? (
              <div className={`${fontSize} leading-relaxed ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                {chapterData.verses.map((verse) => {
                  const highlighted = isHighlighted(verse.verse);
                  const note = hasNote(verse.verse);
                  
                  return (
                    <span
                      key={verse.verse}
                      className={`
                        inline cursor-pointer transition-colors
                        ${highlighted ? getHighlightClass(highlighted.color) : ''}
                        ${selectedVerse === verse.verse ? 'ring-2 ring-indigo-500 ring-offset-2 rounded' : ''}
                        ${note ? 'border-b-2 border-dashed border-indigo-400' : ''}
                      `}
                      onClick={() => handleVerseTap(verse)}
                    >
                      <sup className="text-xs text-indigo-600 font-bold mr-1">{verse.verse}</sup>
                      {verse.text}{' '}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Book className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Bible Text Coming Soon</h3>
                <p className="text-gray-500 mt-2">
                  The KJV Bible text is being imported. In the meantime, you can browse books and chapters.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Verse Action Bar */}
        {selectedVerse && (
          <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 bg-white dark:bg-slate-800 rounded-full shadow-lg border px-2 py-1 flex items-center gap-1 z-50">
            <Button variant="ghost" size="sm" onClick={() => setSelectedVerse(null)}>
              <X className="h-4 w-4" />
            </Button>
            <div className="w-px h-6 bg-gray-200 dark:bg-gray-700" />
            <Button variant="ghost" size="sm" onClick={handleHighlight}>
              <Highlighter className="h-4 w-4 text-yellow-500" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowNoteDialog(true)}>
              <PenLine className="h-4 w-4 text-blue-500" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleBookmark}>
              <Bookmark className="h-4 w-4 text-indigo-500" />
            </Button>
          </div>
        )}

        {/* Note Dialog */}
        <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Note - {selectedBook} {selectedChapter}:{selectedVerse}</DialogTitle>
            </DialogHeader>
            <Textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Write your thoughts..."
              className="min-h-[150px]"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
              <Button onClick={handleAddNote} disabled={!noteContent.trim()}>Save Note</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>

      {/* Bottom Navigation */}
      <nav className={`fixed bottom-0 left-0 right-0 border-t ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-200'}`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2">
            <Button
              variant="ghost"
              size="lg"
              onClick={goToPrevChapter}
              disabled={selectedChapter === 1 && books.findIndex(b => b.name === selectedBook) === 0}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" className="flex flex-col items-center gap-1 h-auto py-2">
                <Home className="h-5 w-5" />
                <span className="text-xs">Home</span>
              </Button>
              <Button variant="ghost" size="sm" className="flex flex-col items-center gap-1 h-auto py-2" onClick={() => setShowBookSelector(true)}>
                <Book className="h-5 w-5" />
                <span className="text-xs">Bible</span>
              </Button>
              <Button variant="ghost" size="sm" className="flex flex-col items-center gap-1 h-auto py-2">
                <Calendar className="h-5 w-5" />
                <span className="text-xs">Plans</span>
              </Button>
              <Button variant="ghost" size="sm" className="flex flex-col items-center gap-1 h-auto py-2">
                <Heart className="h-5 w-5" />
                <span className="text-xs">Prayer</span>
              </Button>
            </div>
            
            <Button
              variant="ghost"
              size="lg"
              onClick={goToNextChapter}
              disabled={selectedChapter === totalChapters && books.findIndex(b => b.name === selectedBook) === books.length - 1}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default BiblePage;
